/**
 * Google Analytics 4 Utilities
 * Handles GA4 initialization, page tracking, and event tracking
 */
import ReactGA from 'react-ga4';

const GA4_MEASUREMENT_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;

/**
 * Initialize Google Analytics 4
 */
export const initGA = () => {
  if (GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    ReactGA.initialize(GA4_MEASUREMENT_ID, {
      testMode: process.env.NODE_ENV === 'development' ? false : false,
    });
    return true;
  }
  return false;
};

/**
 * Track page view
 * @param {string} path - Page path
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  if (GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    ReactGA.send({
      hitType: 'pageview',
      page: path,
      title: title,
    });
  }
};

/**
 * Track custom event
 * @param {string} eventName - Event name
 * @param {Object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (GA4_MEASUREMENT_ID && GA4_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    ReactGA.event(eventName, eventParams);
  }
};

/**
 * Track form submission
 * @param {string} formName - Form name/identifier
 * @param {string} formType - Type of form (contact, application, etc.)
 */
export const trackFormSubmission = (formName, formType) => {
  trackEvent('form_submit', {
    form_name: formName,
    form_type: formType,
  });
};

/**
 * Track button click
 * @param {string} buttonName - Button name/identifier
 * @param {string} location - Location where button was clicked
 */
export const trackButtonClick = (buttonName, location = '') => {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
};

/**
 * Track external link click
 * @param {string} linkUrl - External URL
 * @param {string} linkText - Link text/identifier
 */
export const trackExternalLink = (linkUrl, linkText) => {
  trackEvent('external_link_click', {
    link_url: linkUrl,
    link_text: linkText,
  });
};

/**
 * Track file download
 * @param {string} fileName - File name
 * @param {string} fileType - File type
 */
export const trackFileDownload = (fileName, fileType) => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
  });
};

/**
 * Track video play
 * @param {string} videoName - Video name/identifier
 * @param {string} videoLocation - Location where video is played
 */
export const trackVideoPlay = (videoName, videoLocation) => {
  trackEvent('video_play', {
    video_name: videoName,
    video_location: videoLocation,
  });
};

/**
 * Track job application start
 * @param {string} jobId - Job ID
 * @param {string} jobTitle - Job title
 */
export const trackJobApplicationStart = (jobId, jobTitle) => {
  trackEvent('job_application_start', {
    job_id: jobId,
    job_title: jobTitle,
  });
};

/**
 * Track job application completion
 * @param {string} jobId - Job ID
 * @param {string} jobTitle - Job title
 */
export const trackJobApplicationComplete = (jobId, jobTitle) => {
  trackEvent('job_application_complete', {
    job_id: jobId,
    job_title: jobTitle,
  });
};

/**
 * Track web vitals
 * @param {Object} metric - Web vital metric
 */
export const trackWebVital = (metric) => {
  trackEvent('web_vital', {
    metric_name: metric.name,
    metric_value: metric.value,
    metric_id: metric.id,
    metric_delta: metric.delta,
  });
};

export default {
  initGA,
  trackPageView,
  trackEvent,
  trackFormSubmission,
  trackButtonClick,
  trackExternalLink,
  trackFileDownload,
  trackVideoPlay,
  trackJobApplicationStart,
  trackJobApplicationComplete,
  trackWebVital,
};
